import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { sameData, parseConfiguration } from './configuration';
import './Form.scss';

const createTagFilter = (search) => {
  const lowSearch = search && search.length && search.toLowerCase() || null;
  if (!lowSearch) {
    return null;
  }
  return tags => !!tags && Boolean(tags.find(tag => (tag.toLowerCase().indexOf(lowSearch) >= 0)));
};

//
// Form component
//
class Form extends React.PureComponent {
  static getDerivedStateFromProps(props, state) {
    let newState = null;

    // Form component can be configured whether by a configuration object
    // or by a configuration data computed from the method parseConfiguration
    if (props.configuration !== state.configuration) {
      if (props.configuration) {
        const { __parsedConfiguration, ...configuration } = props.configuration;
        if (__parsedConfiguration) {
          newState = configuration;
        } else {
          newState = parseConfiguration(props.configuration, props.options, props.viewService);
        }
      }
      newState.configuration = props.configuration;
    }

    // If form data changes, update errors and local values
    if (newState || (props.data !== state.data)) {
      if (!newState) {
        newState = {};
      }
      const { validate, getFormData } = { ...state, ...newState };
      newState.data = props.data;
      newState.formData = JSON.parse(JSON.stringify(getFormData(props.data)));
      // If errors not controlled, compute them within the form
      if ((props.errors === undefined) && validate) {
        newState.errors = validate(props.data, props);
      }
    }

    if (props.search !== state.search) {
      if (!newState) {
        newState = {};
      }
      newState.search = props.search;
      newState.tagFilter = createTagFilter(props.search);
    }

    return newState;
  }

  constructor(props) {
    super(props);
    this.state = {};

    this.handleEditorChange = this.handleEditorChange.bind(this);
  }

  handleEditorChange(name, value) {
    const { validate, editors, formData, setFormData } = this.state;
    const editor = editors[name];
    const prevValue = editor.getValue(formData, this.props);
    const v = editor.ensureValue(value, formData, this.props);
    if (prevValue !== v) {
      const newData = JSON.parse(JSON.stringify(formData));
      editor.setValue(newData, v);

      // Check for modified value in depedent editors
      const updateDepedencies = (e) => {
        (e.depedencies || []).forEach((dep) => {
          const pdv = dep.getValue(formData, this.props);
          const dv = dep.ensureValue(pdv, newData, this.props);
          if (pdv !== dv) {
            dep.setValue(newData, dv);
            updateDepedencies(dep);
          }
        });
      };
      updateDepedencies(editor);

      const resultData = setFormData(this.props.data, newData);
      // Collect errors if errors are not provided with props
      if (this.props.errors === undefined) {
        // Uncontrolled errors
        const { errors: oldErrors } = this.state;
        const errors = validate(newData, this.props, oldErrors);
        this.setState({ formData: newData, errors });
        this.props.onFormChange({
          data: resultData,
          errors,
        });
      } else {
        this.setState({ formData: newData });
        this.props.onFormChange(resultData);
      }
    }
  }

  render() {
    const errors = this.state.errors || this.props.errors;
    const {
      editorNames,
      editors,
      formData,
      layout,
      variant,
      hasChildrenAnchor,
      tagFilter,
      renderLayoutComponents,
    } = this.state;
    const { children, id, ...rest } = this.props;
    const filterEditorNames = () => {
      const filteredEditors = [];
      editorNames.forEach((name) => {
        const tags = editors[name].property.tags || [];
        if (!tagFilter || tagFilter(tags)) {
          filteredEditors.push(name);
        }
      });
      return filteredEditors;
    };

    const allProps = {
      ...rest,
      children,
      filteredEditors: tagFilter ? filterEditorNames() : editorNames,
      data: formData,
      onChange: this.handleEditorChange,
      errors,
      formErrors: errors,
    };

    const main = (
      <div className={classnames('grb-form', variant && `grb-form-${variant}`)} id={id}>
        {renderLayoutComponents(layout, allProps)}
      </div>
    );
    return hasChildrenAnchor || !children ? main : (
      <React.Fragment>
        {main}
        {children}
      </React.Fragment>
    );
  }
}

Form.propTypes = {
  viewService: PropTypes.shape({
    getComponent: PropTypes.func,
    getIcon: PropTypes.func,
  }).isRequired,
  configuration: PropTypes.shape({
    variant: PropTypes.string,
    properties: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      labe: PropTypes.string,
      type: PropTypes.string,
      description: PropTypes.string,
      caption: PropTypes.string,
      validators: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
      ])),
    })),
    layout: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.PropTypes.arrayOf(PropTypes.shape({})),
        PropTypes.shape({}),
      ]),
    ),
    __parsedConfiguration: PropTypes.bool, // If already parsed
  }),
  data: PropTypes.shape({}),
  options: PropTypes.shape({}),
  onFormChange: PropTypes.func.isRequired,
  search: PropTypes.string,
  readOnly: PropTypes.bool,
  errors: PropTypes.shape({}),
  validate: PropTypes.func,
  children: PropTypes.node,
  id: PropTypes.string,
};

Form.defaultProps = {
  configuration: {},
  data: {},
  options: {},
  search: undefined,
  readOnly: false,
  errors: undefined,
  validate: undefined,
  children: undefined,
  id: undefined,
};

export { Form as default, sameData, parseConfiguration };
